import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import { useState, useCallback, useEffect, useRef } from "react";

import { AuthConfig } from "../../../setup/context/config/authConfig";
import { useKcToken } from "../../../setup/auth/useKcToken";
import { ROUTES } from "../../../setup/constants";
import { useNavigate } from "react-router-dom";
import { getExternalContracts } from "../../../setup/api/services/contract";
import ContractsListItem from "../contractListItem";
import {
  EContractType,
  EEscalationStatus,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";
import Spinner from "../../../components/common/Spinner";
import Pagination from "../pagination";
import PrimaryButton from "../../../components/common/PrimaryButton";
import SearchInput from "../../../components/common/searchInput";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { endOfDay, format, startOfDay } from "date-fns";
import ContractsFilters from "./filters";
import ContractTypeModal from "../../../components/contract/contractTypeModal";
import { isTwoPartyContract } from "../../../setup/util/twoPartyContract";
import {
  EMPTY_FILTERS,
  IContractsFilters,
  hasNarrowingFilters,
} from "./filters/constants";

// The format the contracts API expects for createdAtFrom / createdAtTo.
const API_DATE_FORMAT = "yyyy-MM-dd HH:mm";

const Contracts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userToken = useKcToken();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState<IContractDetailsResponseData[]>(
    []
  );
  const [tabType, setTabType] = useState("active");

  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [activeTabPage, setActiveTabPage] = useState(1);
  const [prevTabPage, setPrevTabPage] = useState(1);
  const pageSize = 10;
  const [searchValue, setSearchValue] = useState("");
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [filters, setFilters] = useState<IContractsFilters>(EMPTY_FILTERS);
  const [resultType, setResultType] = useState<"complex" | "milestone">(
    "complex"
  );

  const isFiltered = hasNarrowingFilters(filters) || !!searchValue.trim();
  const debouncedSearch = useDebouncedValue(searchValue.trim());
  const debouncedReferenceId = useDebouncedValue(filters.referenceId.trim());

  const organizationCode = useOrganizationCode(userToken) || undefined;

  // Every filter narrows a different result set, so paging starts over on both tabs.
  const resetPaging = () => {
    setPage(1);
    setActiveTabPage(1);
    setPrevTabPage(1);
  };

  const handleFiltersChange = (patch: Partial<IContractsFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    resetPaging();
  };

  const handleFiltersReset = () => {
    setFilters(EMPTY_FILTERS);
    resetPaging();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabType(newValue);
    // Each tab has its own statuses.
    setFilters((prev) => ({ ...prev, status: "" }));
    if (newValue === "finished") {
      setPage(prevTabPage != 1 ? prevTabPage : 1);
    } else {
      setPage(activeTabPage != 1 ? activeTabPage : 1);
    }
  };

  const handlePaginationChange = (value: number) => {
    setPage(value);
    if (tabType === "active") {
      setActiveTabPage(value);
    } else {
      setPrevTabPage(value);
    }
  };

  // Only the latest request may update the list; filters change faster than responses arrive.
  const latestRequest = useRef(0);
  // The text filters arrive debounced as arguments; these apply as soon as they change.
  const { status, dateRange, sort } = filters;

  const fetchContracts = useCallback(
    async (listType: string, contractId: string, referenceId: string) => {
      const requestId = ++latestRequest.current;
      setLoading(true);
      try {
        const baseBody = {
          contractId: contractId,
          referenceTransactionId: referenceId,
          status,
          createdAtFrom: dateRange.from
            ? format(startOfDay(dateRange.from), API_DATE_FORMAT)
            : "",
          createdAtTo: dateRange.to
            ? format(endOfDay(dateRange.to), API_DATE_FORMAT)
            : "",
          sort,
          page: page - 1,
          size: pageSize,
          organizationCode: organizationCode,
        };
        const isCurrent = () => requestId === latestRequest.current;

        // A reference ID belongs to a milestone payment, so like an ID search it can
        // match milestones when no project matches.
        if (!contractId && !referenceId) {
          const response = await getExternalContracts({
            ...baseBody,
            listType: `profile_${listType}`,
            type: EContractType.COMPLEX_CONTRACT,
          });
          if (!isCurrent()) return;
          setContracts(response.data.content);
          setPagesCount(response.data.totalPages);
          setTotalCount(response.data.totalElements);
          setResultType("complex");
        } else {
          const [complexResponse, milestoneResponse] = await Promise.all([
            getExternalContracts({
              ...baseBody,
              listType: `profile_${listType}`,
              type: EContractType.COMPLEX_CONTRACT,
            }),
            getExternalContracts({
              ...baseBody,
              listType,
              type: EContractType.MILESTONE_CONTRACT,
            }),
          ]);
          if (!isCurrent()) return;

          if (complexResponse.data.content.length > 0) {
            setContracts(complexResponse.data.content);
            setPagesCount(complexResponse.data.totalPages);
            setTotalCount(complexResponse.data.totalElements);
            setResultType("complex");
          } else {
            setContracts(milestoneResponse.data.content);
            setPagesCount(milestoneResponse.data.totalPages);
            setTotalCount(milestoneResponse.data.totalElements);
            setResultType("milestone");
          }
        }

        setLoading(false);
      } catch (error) {
        if (requestId === latestRequest.current) setLoading(false);
      }
    },
    [status, dateRange, sort, organizationCode, page]
  );

  useEffect(() => {
    // Skip if the organizationCode isn't ready yet
    if (organizationCode === undefined) {
      return;
    }
    fetchContracts(tabType, debouncedSearch, debouncedReferenceId);
  }, [
    fetchContracts,
    organizationCode,
    tabType,
    debouncedSearch,
    debouncedReferenceId,
  ]);

  return (
    <div className={styles.container}>
      <h1 className={styles.topHeader}>{t("lbl_contracts_list")}</h1>
      <div className={styles.topBar}>
        <div className={styles.tabsGroup}>
          <p
            onClick={(e) => handleTabChange(e, "active")}
            className={`${styles.tab} ${
              tabType === "active" ? styles.tabActive : ""
            }`}>
            {t("lbl_active_contracts")}
          </p>
          <p
            onClick={(e) => handleTabChange(e, "finished")}
            className={`${styles.tab} ${
              tabType === "finished" ? styles.tabActive : ""
            }`}>
            {t("lbl_finished_contracts")}
          </p>
        </div>
        <div className={styles.actions}>
          <SearchInput
            dir="rtl"
            placeholder={t("lbl_search_by_contract_id_or_title")}
            value={searchValue}
            style={{ width: "204px", height: "40px", borderRadius: "10px" }}
            handleChange={(event) => {
              setSearchValue(event.target.value);
              resetPaging();
            }}
          />

          <div className={styles.createButton}>
            {(authorities?.includes("CLIENT_ADMIN_CREATE_CONTRACT") ||
              authorities?.includes("CLIENT_ADMIN") ||
              authorities?.includes("ORGANIZATION_OWNER")) && (
              <PrimaryButton onClick={() => setTypeModalOpen(true)}>
                {t("lbl_create_new_project")}
              </PrimaryButton>
            )}
            <ContractTypeModal
              visible={typeModalOpen}
              onClose={() => setTypeModalOpen(false)}
            />
          </div>
        </div>
      </div>
      <ContractsFilters
        tabType={tabType}
        filters={filters}
        onChange={handleFiltersChange}
        onReset={handleFiltersReset}
      />
      {contracts?.length === 0 ? (
        <>
          {!loading && (
            <div className={styles.emptyWrapper}>
              <div className={styles.emptyContent}>
                <img
                  src="/assets/images/svg/emptyState.svg"
                  alt={t("lbl_no_transactions")}
                  className={styles.emptyIcon}
                />
                <div className={styles.emptyText}>
                  <h5 className={styles.emptyTitle}>
                    {isFiltered
                      ? t("lbl_contracts_no_matches")
                      : t("lbl_no_projects_yet")}
                  </h5>
                  <p className={styles.emptySubtitle}>
                    {isFiltered
                      ? t("lbl_contracts_no_matches_hint")
                      : t("lbl_create_new_project_now")}
                  </p>
                </div>
                {isFiltered ? (
                  <p
                    onClick={() => {
                      setSearchValue("");
                      handleFiltersReset();
                    }}
                    className={styles.emptyCta}>
                    {t("lbl_contracts_filter_reset")}
                  </p>
                ) : authorities?.includes("CLIENT_ADMIN_CREATE_CONTRACT") ||
                  authorities?.includes("CLIENT_ADMIN") ||
                  authorities?.includes("ORGANIZATION_OWNER") ? (
                  <p
                    onClick={() => setTypeModalOpen(true)}
                    className={styles.emptyCta}>
                    {t("lbl_create_new_project")}
                  </p>
                ) : null}
              </div>
            </div>
          )}
          {loading && (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Spinner />
            </div>
          )}
        </>
      ) : authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
        authorities?.includes("CLIENT_ADMIN") ||
        authorities?.includes("ORGANIZATION_OWNER") ? (
        <div className={styles.contractsContainer}>
          {contracts?.map((contract) => (
            <ContractsListItem
              key={(contract as { id: string })?.id}
              title={contract?.itemDetail?.title}
              description={contract?.itemDetail?.description}
              shortId={contract?.shortId}
              status={contract?.status}
              href={ROUTES.CONTRACT_DETAILS?.replace(":id", contract?.id)}
              hasEscalationAction={
                contract?.escalations?.find((e) => !e.resolved)?.status ===
                  EEscalationStatus.RESOLVED_CUSTOMER ||
                contract?.escalations?.find((e) => !e.resolved)?.status ===
                  EEscalationStatus.RESOLVED_PROVIDER
              }
              onClick={() =>
                navigate(
                  ROUTES.CONTRACT_DETAILS?.replace(":id", contract?.id),
                  { state: { from: location.pathname } }
                )
              }
              {...(resultType === "complex"
                ? {
                    milestoneCount: contract?.activeChildrenCount,
                    partiesCount: contract?.activeChildrenPartiesCount,
                    itemPrice: contract?.activeChildrenTotalAmount,
                    contractKind: isTwoPartyContract(contract)
                      ? ("two-party" as const)
                      : ("multi-party" as const),
                  }
                : {
                    partiesCount: contract?.parties?.length,
                    itemPrice: contract?.itemPrice,
                  })}
            />
          ))}
        </div>
      ) : (
        <></>
      )}
      <div
        className={contracts?.length ? styles.paginatorContainer : styles.hide}>
        <Pagination
          countName={t("lbl_project")}
          pagesCount={pagesCount}
          length={totalCount}
          page={page}
          pageSize={pageSize}
          handlePaginationChange={handlePaginationChange}
        />
      </div>
    </div>
  );
};

export default Contracts;
